import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import { t } from '../../utilities/translator';
import {
  updateDatesFilter,
  updateBudgetFilter,
  updateMainFilter,
  updateWhoFilter,
} from '../mainFilter/mainFilterSlice';
import {
  selectSecondaryFilter,
  updateAllSecondaryFilter,
  updateSecondaryFilter,
} from '../secondaryFilter/secondaryFilterSlice';
import { selectDealsListPending, selectTotalDealsCount } from '../dealsList/dealsListSlice';
import { FILTER_RESET_ALL_FILTERS_EVENT } from '../../constants/analyticEvents';
import { pushEvent } from '../../utilities/analytics';
import { selectFilterData } from '../mainFilter/selectors';
import MainTitleComponent from '../../components/MainTitleComponent';
import { getImage } from '../../utilities/iconResolver';
import { getDisplayPrice } from '../../utilities/priceHelper';
import { DATE_TYPES } from '../../constants/constants';
import { updateSearch } from '../mainFilter/searchSlice';
import { differenceInDays } from 'date-fns';
import { parseDate } from '../../utilities/helpers';

export default function NoResult() {
  const dispatch = useDispatch();

  const mainFilter = useSelector(selectFilterData);
  const secondaryFilter = useSelector(selectSecondaryFilter);
  const totalDealsCount = useSelector(selectTotalDealsCount);
  const dealListPending = useSelector(selectDealsListPending);

  const {
    searchEngine: { budget, what, when, where },
  } = mainFilter;

  const { board, duration, facilities, rating, sort, stars, stops, luggage } = secondaryFilter.data;

  const handleClearAllSecondaryFilter = () => {
    dispatch(
      updateAllSecondaryFilter({
        updatedFilters: {
          board: board.map((item) => ({ ...item, active: false })),
          stars: stars.map((item) => ({ ...item, active: false })),
          sort: sort.map((item, ind) => (ind === 0 ? { ...item, active: true } : { ...item, active: false })),
          rating: rating.map((item) => ({ ...item, active: false })),
          facilities: facilities.map((item) => ({ ...item, active: false })),
          stops: stops.map((item) => ({ ...item, active: false })),
          duration: {
            activeTime: duration.maxTime,
            minTime: duration.minTime,
            maxTime: duration.maxTime,
          },
          freeCancellation: false,
          flex: false,
          luggage: luggage && luggage.map(i => ({ ...i, active: false })),
        },
      })
    );
  };

  const handleClearAllMainFilter = () => {
    dispatch(
      updateDatesFilter({
        type: 'month',
        startDate: null,
        endDate: null,
        months: [],
        nights: [],
      })
    );
    dispatch(updateSearch({ when: { startDate: null, endDate: null, months: [], nights: [] } }));

    dispatch(updateBudgetFilter([budget.initial.min, budget.initial.max]));

    dispatch(
      updateMainFilter({
        filter: 'what',
        updatedValue: what.map((item) => ({ ...item, active: false })),
      })
    );

    dispatch(updateWhoFilter({ adults: 2, children: 0, rooms: 1, childAges: [] }));

    dispatch(
      updateMainFilter({
        filter: 'where',
        updatedValue: {
          ...where,
          destinations: where.destinations.map((item) => ({
            ...item,
            active: false,
          })),
        },
      })
    );
  };

  const clearSecondaryFilter = (filter) => {
    if (!filter) return undefined;
    let updatedValue = {};

    switch (filter) {
      case 'board':
        updatedValue = board.map((item) => ({ ...item, active: false }));
        break;
      case 'stars':
        updatedValue = stars.map((item) => ({ ...item, active: false }));
        break;
      case 'sort':
        updatedValue = sort.map((item, ind) => (ind === 0 ? { ...item, active: true } : { ...item, active: false }));
        break;
      case 'rating':
        updatedValue = rating.map((item) => ({ ...item, active: false }));
        break;
      case 'facilities':
        updatedValue = facilities.map((item) => ({ ...item, active: false }));
        break;
      case 'stops':
        updatedValue = stops.map((item) => ({ ...item, active: false }));
        break;
      case 'duration':
        updatedValue = {
          activeTime: duration.maxTime,
          minTime: duration.minTime,
          maxTime: duration.maxTime,
        };
        break;
      case 'freeCancellation':
        updatedValue = false;
        break;
      case 'luggage':
        updatedValue = luggage.map(i => ({ ...i, active: false }));
        break;
      default:
        break;
    }

    dispatch(updateSecondaryFilter({ updatedValue, filter }));
  };

  const clearMainFilter = (filter) => {
    if (!filter) return undefined;

    switch (filter) {
      case 'when':
        dispatch(
          updateDatesFilter({
            startDate: null,
            endDate: null,
            months: [],
            type: DATE_TYPES.MONTH,
            nights: [],
            weekendsOnly: false,
          })
        );
        dispatch(updateSearch({ when: { startDate: null, endDate: null, months: [], nights: [] } }));
        break;
      case 'budget':
        dispatch(updateBudgetFilter([budget.initial.min, budget.initial.max]));
        break;
      case 'what':
        dispatch(
          updateMainFilter({
            filter: 'what',
            updatedValue: what.map((item) => ({ ...item, active: false })),
          })
        );
        break;
      case 'who':
        dispatch(updateWhoFilter({ adults: 2, children: 0, rooms: 1 }));
        break;
      case 'where':
        dispatch(
          updateMainFilter({
            filter: 'where',
            updatedValue: {
              ...where,
              destinations: where.destinations.map((item) => ({
                ...item,
                active: false,
              })),
            },
          })
        );
        break;
      default:
        break;
    }
  };

  const resetAllFilters = () => {
    handleClearAllSecondaryFilter();
    handleClearAllMainFilter();
    pushEvent(FILTER_RESET_ALL_FILTERS_EVENT);
  };

  let selectedDestinations = where?.destinations.filter(({ active }) => active);
  const selectedDestinationsGroups = where?.destinationsGroups.filter(({ active }) => active);
  const activeFacilities = facilities.filter(({ active }) => active) || [];
  const facilitiesLabel = activeFacilities.length > 0
    ? `${activeFacilities[0].label.name || ''}${
        activeFacilities.length > 1 ? ` + ${activeFacilities.length - 1}` : ''
      }`
    : '';

  const getNightsFilterLabel = () => {
    if (when.nights?.length > 0 || (when.type === DATE_TYPES.SPECIFIC && when.startDate && when.endDate)) {
      let label = `${when.nights?.join(', ')} ${t('secondaryFilter.nights_plural', { count: 2 })}`
      if (when.type === DATE_TYPES.SPECIFIC) {
        const nights = differenceInDays(parseDate(when.endDate), parseDate(when.startDate));
        label = nights + ' ' + t('secondaryFilter.nights_plural', { count: nights })
      }

      return (
        <div className="no-result__active-filter">
          <span>{label}</span>
          <i className="ri-close-line text-lg leading-none cursor-pointer" onClick={() => clearMainFilter('when')} />
        </div>
      )
    }
  }

  return (
    <div className="c-row">
      <div className="col-lg-12 flex flex-col gap-8">
        <MainTitleComponent
          destinationName={selectedDestinations.length === 1 ? selectedDestinations[0]?.name : null}
          destinationGroupName={selectedDestinationsGroups.length === 1 ? selectedDestinationsGroups[0]?.name : null}
        />
        {totalDealsCount === 0 && !dealListPending && (
          <>
            <div className="no-result">
              <div className="flex flex-col gap-5 text-center items-center">
                <div className="no-result__icon">
                  <ReactSVG src={getImage('user-sad-line.svg')} />
                </div>
                <div>
                  <h3 className="no-result__title">{t('noResult.title')}</h3>
                  <p className="no-result__text" dangerouslySetInnerHTML={{ __html: selectedDestinations.length === 1 ? t('noResult.messageSingleDestination', { destination: selectedDestinations[0]?.name }) : t('noResult.message') }} />
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {(budget.max !== budget.initial.max || budget.min !== budget.initial.min) && (
                    <div className="no-result__active-filter">
                      <span>{t('noResult.budgetUpTo', { value: getDisplayPrice(budget.max) })}</span>
                      <i className="ri-close-line text-lg leading-none cursor-pointer" onClick={() => clearMainFilter('budget')} />
                    </div>
                  )}
                  {activeFacilities.length > 0 && (
                    <div className="no-result__active-filter">
                      <span>{facilitiesLabel}</span>
                      <i className="ri-close-line text-lg leading-none cursor-pointer" onClick={() => clearSecondaryFilter('facilities')} />
                    </div>
                  )}
                  {board.filter(({ active }) => active).length > 0 && (
                    <div className="no-result__active-filter">
                      <span>{board.filter(({ active }) => active)[0].label}</span>
                      <i className="ri-close-line text-lg leading-none cursor-pointer" onClick={() => clearSecondaryFilter('board')} />
                    </div>
                  )}
                  {stops.filter(({ active }) => active).length > 0 && (
                    <div className="no-result__active-filter">
                      <span>{stops.filter(({ active }) => active)[0].label}</span>
                      <i className="ri-close-line text-lg leading-none cursor-pointer" onClick={() => clearSecondaryFilter('stops')} />
                    </div>
                  )}
                  {getNightsFilterLabel()}
                  {stars.filter(({ active }) => active).length > 0 && (
                    <div className="no-result__active-filter">
                      <span>{`${stars.filter(({ active }) => active).map(({ label }) => label).join(' & ')} ${t('secondaryFilter.stars_plural', { count: 2 })}`}</span>
                      <i className="ri-close-line text-lg leading-none cursor-pointer" onClick={() => clearSecondaryFilter('stars')} />
                    </div>
                  )}
                  {luggage && luggage.filter(({ active }) => active).length > 0 && (
                    <div className="no-result__active-filter">
                      <span>{luggage.filter(({ active }) => active)[0].label}</span>
                      <i className="ri-close-line text-lg leading-none cursor-pointer" onClick={() => clearSecondaryFilter('luggage')} />
                    </div>
                  )}
                </div>
                <div>
                  <button className="no-result__clear-all inline-flex gap-1 items-center justify-center" onClick={resetAllFilters}>
                    <span>{t('noResult.reset_all_filters')}</span>
                    <i className="ri-arrow-right-s-line text-lg leading-none rtl:-scale-x-100" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
